/* import __COLOCATED_TEMPLATE__ from './request.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { cached } from 'tracked-toolbox';
import { HTTP_METHODS } from 'embercom/lib/custom-authentication/request-helper';
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import type Action from 'embercom/models/workflow-connector/action';
import { ContentType, SAFE_ATTRIBUTE_IDENTIFIERS } from 'embercom/models/workflow-connector/action';
import type Token from 'embercom/models/custom-authentication/token';
import type ParamDescriptor from 'embercom/models/workflow-connector/param-descriptor';
import type Attribute from 'embercom/models/attribute';
//@ts-ignore
import { sanitizeUrl } from '@intercom/pulse/lib/sanitize';

const PENDING_TOKEN_ID = null;

interface Args {
  workflowAction: Action;
  customAuthenticationTokens: Token[];
  nextSection: (sectionId: string) => void;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

interface ContentTypeOption {
  value: ContentType;
  text: string;
}

export default class Request extends Component<Signature> {
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service declare attributeService: any;
  @service declare intl: any;

  @tracked selectedPreviewTab = 'headers';
  @tracked isBodyAvailable = ['post', 'put', 'patch', 'delete'].includes(
    this.args.workflowAction.httpMethod,
  );

  @tracked isMcpAction = !!this.args.workflowAction.mcpServerId;
  @tracked customAuthenticationTokens = this.args.customAuthenticationTokens;
  @tracked selectedTokenId = this.args.workflowAction.tokenId || undefined;
  @tracked urlBlocks;
  @tracked bodyBlocks;
  @tracked showWriteActionConfirmation = ['post', 'put', 'delete', 'patch'].includes(
    this.args.workflowAction.httpMethod,
  );
  @tracked bodyContentTypeOptions: ContentTypeOption[];

  private readonly supportedContentTypes = [
    ContentType.JSON,
    ContentType.TEXT,
    ContentType.FORM_URLENCODED,
  ];

  private readonly contentTypeTranslations = {
    placeholder: {
      [ContentType.JSON]: 'workflow-connector.builder.body.request.request-body.placeholder-json',
      [ContentType.TEXT]: 'workflow-connector.builder.body.request.request-body.placeholder-text',
      [ContentType.FORM_URLENCODED]:
        'workflow-connector.builder.body.request.request-body.placeholder-form',
      [ContentType.CUSTOM]:
        'workflow-connector.builder.body.request.request-body.placeholder-custom',
    },
    description: {
      [ContentType.JSON]: 'workflow-connector.builder.body.request.request-body.description-json',
      [ContentType.TEXT]: 'workflow-connector.builder.body.request.request-body.description-text',
      [ContentType.FORM_URLENCODED]:
        'workflow-connector.builder.body.request.request-body.description-form',
      [ContentType.CUSTOM]:
        'workflow-connector.builder.body.request.request-body.description-custom',
    },
  };

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.urlBlocks = this.workflowActionURLToBlocks();
    this.bodyBlocks = this.workflowActionBodyToBlocks();
    this.bodyContentTypeOptions = this.getContentTypeOptions();

    if (this.args.workflowAction.httpMethod === null) {
      this.args.workflowAction.httpMethod = 'get';
    }
  }

  get app() {
    return this.appService.app;
  }

  get canUseClientSideActions(): boolean {
    return this.appService.app.canUseFeature('client-side-actions');
  }

  get executionTypeOptions() {
    return [
      {
        text: this.intl.t('workflow-connector.builder.body.general.execution-type.server-side'),
        value: 'server_side',
        description: this.intl.t(
          'workflow-connector.builder.body.general.execution-type.server-side-description',
        ),
      },
      {
        text: this.intl.t('workflow-connector.builder.body.general.execution-type.client-side'),
        value: 'client_side',
        description: this.intl.t(
          'workflow-connector.builder.body.general.execution-type.client-side-description',
        ),
      },
    ];
  }

  get clientSideCodeExample(): string {
    let fnName = this.args.workflowAction.clientFunctionName || 'yourFunctionName';
    let params = this.args.workflowAction.paramDescriptors;

    let argsComment = this.intl.t(
      'workflow-connector.builder.body.general.client-side.code-example.args-comment',
    );
    if (params && params.length > 0) {
      let paramNames = params.map((p) => p.name).join(', ');
      let argsPrefix = this.intl.t(
        'workflow-connector.builder.body.general.client-side.code-example.args-with-params-prefix',
      );
      argsComment = `${argsPrefix} { ${paramNames} }`;
    }
    let yourCodeComment = this.intl.t(
      'workflow-connector.builder.body.general.client-side.code-example.your-code-comment',
    );

    return `window.${fnName} = function(args, resolve, reject) {
  ${argsComment}
  ${yourCodeComment}
  resolve({ result: 'value' });
}`;
  }

  @action
  setExecutionType(value: 'server_side' | 'client_side') {
    this.args.workflowAction.executionType = value;
  }

  get tokenList() {
    return [{ items: this.groupListItems }];
  }

  get hasUnusedParamDescriptors() {
    if (this.isMcpAction) {
      return false;
    }
    return this.args.workflowAction.unUsedParamDescriptors.length > 0;
  }

  get groupListItems() {
    return this.customAuthenticationTokens.map((token) => {
      return {
        value: token.id,
        text: token.name,
      };
    });
  }

  workflowActionURLToBlocks() {
    return [
      {
        type: 'paragraph',
        text: this.args.workflowAction.url || '',
        modelKey: 'common/blocks/paragraph',
      },
    ];
  }

  get localAttributes() {
    let attributes = this.args.workflowAction.paramDescriptors.map((paramDescriptor) => {
      return {
        identifier: paramDescriptor.name,
        name: paramDescriptor.name,
        templatable_identifier: paramDescriptor.name,
        noFallback: true,
      };
    });

    return [
      {
        heading: this.intl.t('workflow-connector.builder.body.request.fin-data.label'),
        attributes,
      },
    ];
  }

  get isInvalid() {
    return Boolean(this.args.workflowAction.installUrlErrors);
  }

  workflowActionBodyToBlocks() {
    if (!this.args.workflowAction.body) {
      return [];
    }

    let bodyLines = this.args.workflowAction.body.split('\n');
    let bodyBlocks = bodyLines.map((line) => {
      return {
        type: 'paragraph',
        text: line,
        class: 'no-margin',
        modelKey: 'common/blocks/paragraph',
      };
    });
    return bodyBlocks;
  }

  get supportedHttpMethods() {
    return HTTP_METHODS;
  }

  get selectedToken() {
    return this.customAuthenticationTokens?.find((token) => token.id === this.selectedTokenId);
  }

  get isAppPackageAction() {
    return this.args.workflowAction.appPackageCode !== null;
  }

  get isFinWriteAction() {
    return this.args.workflowAction.finWriteAction;
  }

  private findContentTypeHeader() {
    let headers = this.args.workflowAction.headers || [];
    return headers.find((header) => header.name.toLowerCase() === 'content-type');
  }

  get bodyContentTypeLabel(): ContentType {
    let existingContentTypeHeader = this.findContentTypeHeader();

    if (existingContentTypeHeader?.value) {
      if (this.supportedContentTypes.includes(existingContentTypeHeader.value as ContentType)) {
        return existingContentTypeHeader.value as ContentType;
      }
      return ContentType.CUSTOM;
    }

    return ContentType.JSON;
  }

  get requestBodyPlaceholder(): string {
    let key =
      this.contentTypeTranslations.placeholder[this.bodyContentTypeLabel] ||
      this.contentTypeTranslations.placeholder[ContentType.JSON];
    return this.intl.t(key);
  }

  get requestBodyTooltip(): string {
    let key =
      this.contentTypeTranslations.description[this.bodyContentTypeLabel] ||
      this.contentTypeTranslations.description[ContentType.JSON];
    return this.intl.t(key);
  }

  get hasInvalidBody() {
    return !this.args.workflowAction.isRequestBodyValid;
  }

  get bodyValidationMessage() {
    if (this.hasInvalidBody) {
      return this.intl.t('workflow-connector.builder.body.request.body.validation-error-json');
    }
    return null;
  }

  get isHeaderTemplatingFeatureEnabled() {
    return this.appService.app.canUseFeature('data-connector-header-templating');
  }

  get isMultiTokenEnabled() {
    return this.appService.app.canUseFeature('tdf-data-connectors-multi-token');
  }

  get tokenConfigs(): Array<Token | { id: null }> {
    return this.args.workflowAction.tokenIds.map((tokenId) => {
      if (tokenId === PENDING_TOKEN_ID) {
        return { id: null };
      }

      return this.customAuthenticationTokens.find((t) => t.id === tokenId)!;
    });
  }

  @cached
  get unprotectedAttributes() {
    return this.attributeService.attributes
      .filter(
        (attr: Attribute) =>
          !attr.preventUpdateFromMessenger && !SAFE_ATTRIBUTE_IDENTIFIERS.includes(attr.identifier),
      )
      .map((attr: Attribute) => attr.identifier)
      .sort();
  }

  @action
  toggleFinWriteAction() {
    this.args.workflowAction.toggleFinWriteAction();
  }

  @action
  previousSection() {
    this.args.nextSection('general_section');
  }

  @action
  nextSection() {
    // Client-side actions skip testing and go directly to logs
    if (this.args.workflowAction.isClientSide) {
      this.args.nextSection('logs');
    } else {
      this.args.nextSection('testing_section');
    }
  }

  private updateContentTypeHeader(value: ContentType) {
    if (value === ContentType.CUSTOM) {
      return;
    }

    let headers = this.args.workflowAction.headers || [];
    let existingHeader = this.findContentTypeHeader();

    if (existingHeader) {
      this.replaceContentTypeHeader(headers, existingHeader, value);
    } else {
      this.addContentTypeHeader(headers, value);
    }
  }

  private replaceContentTypeHeader(headers: any, existingHeader: any, value: ContentType) {
    let index = headers.indexOf(existingHeader);
    let newHeader = this.store.createFragment('workflow-connector/action-header', {
      name: existingHeader.name,
      value,
    });
    headers.removeObject(existingHeader);
    headers.insertAt(index, newHeader);
  }

  private addContentTypeHeader(headers: any, value: ContentType) {
    let header = this.store.createFragment('workflow-connector/action-header', {
      name: 'Content-Type',
      value,
    });

    if (headers.length === 0) {
      this.args.workflowAction.headers = [header];
    } else {
      headers.pushObject(header);
    }
  }

  @action
  setActionHttpMethod(httpMethod: string) {
    this.args.workflowAction.httpMethod = httpMethod;
    this.isBodyAvailable = ['post', 'put', 'patch', 'delete'].includes(httpMethod);
    if (!this.isBodyAvailable) {
      this.selectedPreviewTab = 'headers';
    } else {
      this.updateContentTypeHeader(this.bodyContentTypeLabel);
    }
    this.showWriteActionConfirmation = ['post', 'put', 'delete', 'patch'].includes(
      this.args.workflowAction.httpMethod,
    );
    this.args.workflowAction.codeBlockExecutionTestResult = null;
  }

  @action
  changePreviewTab(selectedTab: string) {
    this.selectedPreviewTab = selectedTab;
  }

  @action
  onUrlComposerChange(newBlocksDoc: { blocks: { text: string }[] }) {
    let blocks = newBlocksDoc.blocks;
    let processedBlocks = sanitizeUrl(blocks[0].text);
    this.args.workflowAction.url = processedBlocks.toString();
    this.args.workflowAction.codeBlockExecutionTestResult = null;
  }

  @action
  onBodyComposerChange(newBlocksDoc: { blocks: { text: string }[] }) {
    let blocks = newBlocksDoc.blocks;
    let blockTexts = blocks.map((block) => block.text);
    let processedBlocks = blockTexts.join('\n');
    this.args.workflowAction.body = processedBlocks.toString();
    this.args.workflowAction.codeBlockExecutionTestResult = null;
  }

  @action
  updateWorkflowToken(tokenId: string) {
    this.args.workflowAction.tokenId = tokenId;
    this.selectedTokenId = tokenId;
    this.args.workflowAction.codeBlockExecutionTestResult = null;
  }

  @action
  clearSelectedToken() {
    this.args.workflowAction.tokenId = null;
    this.selectedTokenId = undefined;
  }

  @action
  onAddPendingToken(): void {
    this.args.workflowAction.tokenIds = [...this.args.workflowAction.tokenIds, PENDING_TOKEN_ID];
  }

  @action
  onTokenChange(index: number, newTokenId: string | null): void {
    let newToken = this.customAuthenticationTokens.find((t) => t.id === newTokenId);
    let isStripeToken = newToken?.isStripeToken;

    if (isStripeToken) {
      this.args.workflowAction.tokenIds = [newTokenId];
      this.args.workflowAction.headers.clear();
    } else {
      this.args.workflowAction.tokenIds = [
        ...this.args.workflowAction.tokenIds.slice(0, index),
        newTokenId,
        ...this.args.workflowAction.tokenIds.slice(index + 1),
      ];
    }

    this.args.workflowAction.codeBlockExecutionTestResult = null;
  }

  @action
  onRemoveToken(index: number): void {
    this.args.workflowAction.tokenIds = [
      ...this.args.workflowAction.tokenIds.slice(0, index),
      ...this.args.workflowAction.tokenIds.slice(index + 1),
    ];

    this.args.workflowAction.codeBlockExecutionTestResult = null;
  }

  @action
  updateParamDescriptor(
    index: string,
    name: string,
    description: string,
    type: string,
    required: boolean,
    defaultValue: string,
  ) {
    if (index) {
      let paramDescriptor = this.args.workflowAction.paramDescriptors.find((x) => x.name === index);
      if (paramDescriptor) {
        paramDescriptor.setProperties({
          name,
          description,
          type,
          required,
          defaultValue,
        });
      }
    } else {
      // For client-side actions, set enabled=true and source='fin' by default
      // so parameters are properly included in arguments
      let isClientSide = this.args.workflowAction.isClientSide;
      this.args.workflowAction.paramDescriptors.pushObject({
        name,
        description,
        type,
        required,
        defaultValue,
        enabled: isClientSide ? true : undefined,
        source: isClientSide ? 'fin' : undefined,
      } as ParamDescriptor);
    }

    // Update body arguments when param descriptors change for client-side actions
    if (this.args.workflowAction.isClientSide) {
      this.updateClientSideBodyArguments();
    }
  }

  @action
  updateBodyArguments() {
    if (this.args.workflowAction.isClientSide) {
      this.updateClientSideBodyArguments();
      return;
    }

    if (!this.isMcpAction) {
      return;
    }
    let bodyObject = JSON.parse(this.args.workflowAction.body || '{}');
    let toolArguments = bodyObject.arguments ?? bodyObject.params?.arguments;

    if (!toolArguments) {
      return;
    }

    this.args.workflowAction.paramDescriptors.forEach((paramDescriptor) => {
      let key = paramDescriptor.name;
      let formattedValue = this.getFormattedValueForParam(paramDescriptor);
      toolArguments[key] = formattedValue;
    });

    let updatedBody = JSON.stringify(bodyObject);

    this.args.workflowAction.body = updatedBody;

    // Update the body blocks for the composer
    this.bodyBlocks = this.workflowActionBodyToBlocks();

    this.bodyBlocks = [...this.bodyBlocks];
  }

  private updateClientSideBodyArguments() {
    let paramDescriptors = this.args.workflowAction.paramDescriptors;

    if (!paramDescriptors || paramDescriptors.length === 0) {
      this.args.workflowAction.body = '';
      return;
    }

    let args: Record<string, string | number | boolean> = {};
    paramDescriptors.forEach((paramDescriptor) => {
      let key = paramDescriptor.name;
      let formattedValue = this.getFormattedValueForParam(paramDescriptor);
      args[key] = formattedValue;
    });

    this.args.workflowAction.body = JSON.stringify({ arguments: args });
  }

  @action
  deleteParamDescriptor(index: string) {
    let paramDescriptor = this.args.workflowAction.paramDescriptors.find((x) => x.name === index);
    if (paramDescriptor) {
      this.args.workflowAction.paramDescriptors.removeObject(paramDescriptor);
    }
  }

  @action
  onBodyContentTypeSelect(selectedType: ContentType) {
    this.updateContentTypeHeader(selectedType);
  }

  private getContentTypeOptions() {
    return [
      { value: ContentType.JSON, text: 'JSON' },
      {
        value: ContentType.TEXT,
        text: this.intl.t('workflow-connector.builder.body.request.request-body.content-type-text'),
      },
      {
        value: ContentType.FORM_URLENCODED,
        text: this.intl.t('workflow-connector.builder.body.request.request-body.content-type-form'),
      },
      {
        value: ContentType.CUSTOM,
        text: this.intl.t(
          'workflow-connector.builder.body.request.request-body.content-type-custom',
        ),
      },
    ];
  }

  private getFormattedValueForParam(paramDescriptor: ParamDescriptor) {
    let key = paramDescriptor.name;
    let source = paramDescriptor.source || 'fin';
    let value = paramDescriptor.value;

    if (source === 'custom') {
      return value;
    } else if (source === 'attribute' && value) {
      return `{{${value}}}`;
    } else {
      //  'fin' source and fallback
      return `{{${key}}}`;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::Builder::Body::Sections::Request': typeof Request;
    'workflow-connector/builder/body/sections/request': typeof Request;
  }
}
